/**
 * Theme system for Machina
 * Inspired by Robert Gould's book cover aesthetic:
 * - Bold, readable typography
 * - Rich, saturated colors
 * - Medieval/fantasy glyphs and ornamentation
 */

export interface MachinaTheme {
  id: string;
  name: string;
  description: string;
  
  colors: {
    // Primary palette
    primary: string;
    primaryDark: string;
    primaryLight: string;
    
    // Secondary/accent
    secondary: string;
    accent: string;
    
    // Semantic colors
    success: string;
    warning: string;
    error: string;
    info: string;
    
    // Neutrals
    background: string;
    backgroundElevated: string;
    surface: string;
    surfaceElevated: string;
    
    // Text
    text: string;
    textSecondary: string;
    textMuted: string;
    textInverse: string;
    
    // Borders
    border: string;
    borderLight: string;
    borderFocus: string;
    
    // Game-specific
    dmOnly: string;
    dmChat: string;
    playerOnly: string;
    aiSuggestion: string;
    
    // Grid/map
    gridLine: string;
    fogOfWar: string;
    tokenHighlight: string;

    // Dice colors
    diceD4: string;
    diceD6: string;
    diceD8: string;
    diceD10: string;
    diceD12: string;
    diceD20: string;

    // Extended palette
    purple: string;
    cyan: string;
    orange: string;
    pink: string;
  };
  
  typography: {
    // Font families
    fontDisplay: string;  // Headings, UI chrome
    fontBody: string;     // Body text, chat
    fontMono: string;     // Code, dice rolls
    fontGlyph: string;    // Decorative glyphs
    
    // Sizes
    fontSizeXs: string;
    fontSizeSm: string;
    fontSizeMd: string;
    fontSizeLg: string;
    fontSizeXl: string;
    fontSize2xl: string;
    fontSize3xl: string;
    
    // Weights
    fontWeightNormal: number;
    fontWeightMedium: number;
    fontWeightBold: number;
    fontWeightBlack: number;
    
    // Line heights
    lineHeightTight: number;
    lineHeightNormal: number;
    lineHeightRelaxed: number;
  };
  
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
    '3xl': string;
  };
  
  borders: {
    radiusNone: string;
    radiusSm: string;
    radiusMd: string;
    radiusLg: string;
    radiusFull: string;
    
    widthThin: string;
    widthNormal: string;
    widthThick: string;
  };
  
  shadows: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
  
  effects: {
    transitionFast: string;
    transitionNormal: string;
    transitionSlow: string;
  };

  /**
   * Decorative elements configuration
   * Controls visual ornamentation like scrollwork, borders, and flourishes
   */
  decorations: {
    /** Show corner flourishes on panels */
    cornerFlourishes: boolean;
    /** Show runic/decorative borders */
    decorativeBorders: boolean;
    /** Show section dividers with motifs */
    sectionDividers: boolean;
    /** The motif style for decorations */
    motif: 'chaos-star' | 'celtic-knot' | 'vine' | 'geometric' | 'runes' | 'none';
    /** Corner flourish opacity (0-1) */
    flourishOpacity: number;
    /** Border pattern type */
    borderPattern: 'solid' | 'dashed' | 'dotted' | 'ornate';
    /** Header style */
    headerStyle: 'plain' | 'ornate' | 'underlined';
    /** Panel style */
    panelStyle: 'flat' | 'beveled' | 'inset' | 'glowing';
  };
}

/**
 * Default theme: "Obsidian Court"
 * Ornate dark sword & sorcery — decadent dying-empire decor
 * Deep blacks, silver/platinum, purple undertones, blood red accents
 */
export const defaultTheme: MachinaTheme = {
  id: 'obsidian-court',
  name: 'Obsidian Court',
  description: 'Dark and decadent ornate sword & sorcery',

  colors: {
    // Blood-red primary - hungry rune-blade
    primary: '#8b1538',
    primaryDark: '#5c0f26',
    primaryLight: '#b32346',

    // Gold accent - imperial wealth
    secondary: '#d4a574',
    accent: '#c9a227',

    // Semantic colors
    success: '#2d8a4e',   // Muted forest green
    warning: '#dc143c',   // Crimson blood
    error: '#8b0000',     // Dark blood
    info: '#6366f1',      // Indigo arcane

    // Deep blacks with warm gold undertones
    background: '#0c0a08',
    backgroundElevated: '#141210',
    surface: '#1a1814',
    surfaceElevated: '#24201a',

    // Text - warm parchment tones
    text: '#f0ece4',
    textSecondary: '#c8b8a4',
    textMuted: '#806b5f',
    textInverse: '#0c0a08',

    // Borders - warm gold undertones
    border: '#2a2420',
    borderLight: '#1e1a18',
    borderFocus: '#c9a227',

    // Game-specific
    dmOnly: '#5c4a1d',     // Deep gold
    dmChat: '#b8860b',     // Dark goldenrod
    playerOnly: '#3d3520', // Bronze
    aiSuggestion: '#6b5a2a', // Antique gold

    // Grid/map
    gridLine: '#1e1a14',
    fogOfWar: '#080604',
    tokenHighlight: '#c9a227',

    // Dice colors - gold and crimson palette
    diceD4: '#dc143c',   // Crimson
    diceD6: '#c0c0c0',   // Silver
    diceD8: '#6366f1',   // Indigo
    diceD10: '#c9a227',  // Gold
    diceD12: '#8b0000',  // Blood
    diceD20: '#ffd700',  // Bright gold

    // Extended palette
    purple: '#9333ea',
    cyan: '#06b6d4',
    orange: '#d4a574',   // Warm gold
    pink: '#c026d3',
  },

  typography: {
    // Elegant serifs for the decadent empire
    fontDisplay: '"Cinzel", Georgia, "Times New Roman", serif',
    fontBody: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontMono: '"JetBrains Mono", "Fira Code", Consolas, monospace',
    fontGlyph: 'Georgia, "Times New Roman", serif',

    fontSizeXs: '0.75rem',   // 12px
    fontSizeSm: '0.875rem',  // 14px
    fontSizeMd: '1rem',      // 16px
    fontSizeLg: '1.125rem',  // 18px
    fontSizeXl: '1.25rem',   // 20px
    fontSize2xl: '1.5rem',   // 24px
    fontSize3xl: '2rem',     // 32px

    fontWeightNormal: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,     // Slightly lighter for elegance
    fontWeightBlack: 700,

    lineHeightTight: 1.2,
    lineHeightNormal: 1.5,
    lineHeightRelaxed: 1.75,
  },

  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
    '3xl': '4rem',   // 64px
  },

  borders: {
    radiusNone: '0',
    radiusSm: '2px',       // Sharp corners for angular feel
    radiusMd: '4px',
    radiusLg: '6px',
    radiusFull: '9999px',

    widthThin: '1px',
    widthNormal: '1px',    // Thin elegant lines
    widthThick: '2px',
  },

  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.4)',
    md: '0 4px 12px rgba(0, 0, 0, 0.5)',
    lg: '0 10px 24px rgba(0, 0, 0, 0.6)',
    xl: '0 20px 40px rgba(0, 0, 0, 0.7)',
  },

  effects: {
    transitionFast: '150ms ease-out',
    transitionNormal: '250ms ease-out',
    transitionSlow: '400ms ease-out',
  },

  decorations: {
    cornerFlourishes: true,
    decorativeBorders: true,
    sectionDividers: true,
    motif: 'chaos-star',
    flourishOpacity: 0.35,
    borderPattern: 'ornate',
    headerStyle: 'ornate',
    panelStyle: 'glowing',
  },
};
